'use client';

import { useParams, useRouter } from 'next/navigation';
import { FC, Key } from 'react';
import StudioPreviewLanguageSelector from '@/components/studio/studio-page-content/studio-preview/studio-preview-language-selector';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Image,
} from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import { usePersistUserLocale } from '@/hooks/use-persist-user-locale';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import { setLocaleCookie } from '@/utils/locale-cookie';
import { resolveLocalizedPathname } from '@/utils/locale-href';

type LanguageSelectorPresentation = 'header' | 'mobileMenu';

type LanguageSelectorProps = {
  presentation?: LanguageSelectorPresentation;
};

const FLAG_CDN = (code: string) =>
  `https://flagcdn.com/${code.toLowerCase() === 'en' ? 'gb' : code.toLowerCase()}.svg`;

const LanguageSelectorLive: FC<LanguageSelectorProps> = ({ presentation = 'header' }) => {
  const router = useRouter();
  const params = useParams();
  const coreConfig = useCoreConfig();
  const { changeMyCartLocale } = useMyCart();
  const { changeFavoritesLocale } = useFavorites();
  const { isAuthenticated } = useKeycloak();
  const persistUserLocale = usePersistUserLocale();

  const currentLanguage = (
    (params?.locale as string | undefined) ??
    coreConfig.defaultLanguageCode ??
    'en'
  ).toLowerCase();
  const currentLanguageLabel =
    coreConfig.supportedLanguages.find(language => language.code.toLowerCase() === currentLanguage)
      ?.label ?? currentLanguage.toUpperCase();

  const isMobileMenu = presentation === 'mobileMenu';
  const flagWidth = isMobileMenu ? 28 : 20;
  const flagHeight = isMobileMenu ? 21 : 15;

  const handleChange = (key: Key) => {
    const newLocale = key.toString();
    if (isAuthenticated) {
      // `persistUserLocale` writes cookie + navigates inside `onMutate` (optimistic).
      persistUserLocale(newLocale);
    } else {
      setLocaleCookie(newLocale);
      const target = resolveLocalizedPathname(newLocale, coreConfig.defaultLanguageCode);
      const search = typeof window !== 'undefined' ? window.location.search : '';
      router.replace(`${target}${search}`);
    }
    changeMyCartLocale(newLocale);
    changeFavoritesLocale(newLocale);
  };

  if (coreConfig.supportedLanguages.length <= 1) {
    return null;
  }

  return (
    <Dropdown className="dropdown-custom" classNames={{ content: 'min-w-0' }}>
      <DropdownTrigger>
        <Button
          variant="light"
          aria-label={`Select language, current: ${currentLanguageLabel}`}
          className={cn('min-w-0 text-inherit', isMobileMenu ? 'px-2 py-1.5' : 'p-2')}
        >
          <div className={cn('flex items-center gap-2', isMobileMenu && 'text-base')}>
            <Image
              alt={`${currentLanguage} flag`}
              src={FLAG_CDN(currentLanguage)}
              width={flagWidth}
              height={flagHeight}
              style={{ borderRadius: 0 }}
            />
          </div>
        </Button>
      </DropdownTrigger>

      <DropdownMenu
        aria-label="Language Selector"
        className="dropdown-menu-custom"
        onAction={handleChange}
      >
        {coreConfig.supportedLanguages.map(language => (
          <DropdownItem
            key={language.code}
            value={language.code}
            textValue={language.label}
            className={cn(currentLanguage === language.code ? 'bg-gray-200' : 'bg-transparent')}
          >
            <div
              className={cn(
                'flex items-center justify-start gap-2 px-2',
                isMobileMenu && 'py-0.5 text-base',
              )}
            >
              <Image
                alt={`${language.code} flag`}
                src={FLAG_CDN(language.code)}
                width={flagWidth}
                height={flagHeight}
                style={{ borderRadius: 0 }}
              />
              {language.label}
            </div>
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

const LanguageSelector: FC<LanguageSelectorProps> = props => {
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  if (isStudioPreview) {
    return <StudioPreviewLanguageSelector presentation={props.presentation} />;
  }
  return <LanguageSelectorLive {...props} />;
};

export default LanguageSelector;
